import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Wallet, Coins, CheckCircle2, Clock, XCircle, Mail } from "lucide-react";
import { api, fmtCoins, fmtUsd, coinsToUsd, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";

const TIERS = [5, 10, 25, 50, 100];
const statusStyle = {
  pending: { icon: Clock, cls: "text-amber-neon border-amber-500/40" },
  paid: { icon: CheckCircle2, cls: "text-lime-neon border-emerald-500/40" },
  rejected: { icon: XCircle, cls: "text-[#FF007A] border-pink-500/40" },
  failed: { icon: XCircle, cls: "text-[#FF007A] border-pink-500/40" },
};

export default function Cashout() {
  const { user, setUser } = useAuth();
  const [amount, setAmount] = useState(5);
  const [paypal, setPaypal] = useState(user?.paypal_email || "");
  const [busy, setBusy] = useState(false);
  const [payouts, setPayouts] = useState([]);

  const load = () => api.get("/payouts").then((r) => setPayouts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const usd = coinsToUsd(user?.coins);

  const submit = async () => {
    if (!paypal) return toast.error("Enter your PayPal email");
    if (amount > usd) return toast.error("Amount exceeds your balance");
    setBusy(true);
    try {
      const { data } = await api.post("/payouts", { amount_usd: amount, paypal_email: paypal });
      setUser(data.user);
      toast.success(`Payout of ${fmtUsd(amount)} requested — pending admin approval`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="cashout-page">
      <div>
        <p className="eyebrow mb-1">/ WITHDRAW</p>
        <h1 className="font-display font-black text-2xl sm:text-3xl">PayPal Cashout</h1>
        <p className="text-slate-400 text-sm mt-1">Withdraw your earnings to PayPal. Minimum $5.00.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 neon-card profit-card p-6">
          <div className="flex items-center gap-3 mb-5">
            <img src="https://images.unsplash.com/photo-1652017687934-d14ec6280dd5?crop=entropy&cs=srgb&fm=jpg&q=80&w=120" alt="PayPal" className="w-12 h-12 rounded-lg object-cover border border-slate-700" />
            <div>
              <p className="font-display font-bold">Instant PayPal Payout</p>
              <p className="text-xs text-slate-400">Available balance: <span className="text-lime-neon font-mono-num">{fmtUsd(usd)}</span></p>
            </div>
          </div>

          <p className="eyebrow mb-2">Select amount</p>
          <div className="flex flex-wrap gap-2 mb-5">
            {TIERS.map((t) => (
              <button
                key={t}
                onClick={() => setAmount(t)}
                disabled={t > usd}
                data-testid={`payout-tier-${t}`}
                className={`px-4 py-2 rounded-lg font-mono-num font-semibold text-sm border transition disabled:opacity-30 disabled:cursor-not-allowed ${
                  amount === t ? "bg-emerald-400/15 text-lime-neon border-emerald-500/50" : "border-slate-700 text-slate-300 hover:border-slate-500"
                }`}
              >
                ${t}
              </button>
            ))}
          </div>

          <p className="eyebrow mb-2">PayPal email</p>
          <div className="relative mb-5">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              type="email"
              placeholder="you@paypal.com"
              value={paypal}
              onChange={(e) => setPaypal(e.target.value)}
              data-testid="paypal-email-input"
              className="pl-10 bg-[#07090E] border-slate-700 h-11"
            />
          </div>

          <div className="rounded-xl bg-[#07090E] border border-slate-700 p-4 flex items-center justify-between mb-5">
            <div className="text-sm">
              <p className="text-slate-400">You withdraw</p>
              <p className="font-mono-num font-bold text-xl mt-1">{fmtUsd(amount)}</p>
            </div>
            <div className="text-right text-sm">
              <p className="text-slate-400">Coins used · Fee</p>
              <p className="font-mono-num mt-1">{fmtCoins(amount * 1000)} · <span className="text-lime-neon">$0.00</span></p>
            </div>
          </div>

          <Button onClick={submit} disabled={busy || usd < 5} data-testid="submit-payout-button" className="w-full btn-neon h-12 text-base">
            <Wallet className="w-5 h-5 mr-1" /> {busy ? "Submitting…" : `Request ${fmtUsd(amount)} payout`}
          </Button>
          <p className="text-xs text-slate-500 text-center mt-3">Payouts are reviewed by admin, then sent via PayPal Payouts (sandbox).</p>
        </div>

        <div className="neon-card p-5">
          <h3 className="font-display font-bold mb-4">How it works</h3>
          <ol className="space-y-4">
            {["Reach at least $5.00 in coins", "Enter your PayPal email & submit", "Admin approves your request", "Money lands in your PayPal"].map((s, i) => (
              <li key={i} className="flex gap-3 text-sm text-slate-300">
                <span className="w-6 h-6 shrink-0 rounded-full bg-cyan-400/10 text-cyan-neon flex items-center justify-center font-mono-num text-xs">{i + 1}</span>
                {s}
              </li>
            ))}
          </ol>
        </div>
      </div>

      <div className="neon-card p-5">
        <h3 className="font-display font-bold mb-4">Payout History</h3>
        {payouts.length === 0 ? (
          <p className="text-slate-500 text-sm py-6 text-center">No payouts yet.</p>
        ) : (
          <div className="divide-y divide-slate-800/70">
            {payouts.map((p) => {
              const st = statusStyle[p.status] || statusStyle.pending;
              const Icon = st.icon;
              return (
                <div key={p.payout_id} className="flex items-center justify-between py-3" data-testid="payout-row">
                  <div>
                    <p className="font-mono-num font-semibold">{fmtUsd(p.amount_usd)}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{p.paypal_email} · {new Date(p.created_at).toLocaleDateString()}</p>
                  </div>
                  <Badge className={`bg-transparent border ${st.cls} capitalize gap-1`}>
                    <Icon className="w-3.5 h-3.5" /> {p.status}
                  </Badge>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
